import "core-js/stable";
import "regenerator-runtime/runtime";
import "whatwg-fetch";
import "./src/helpers/polyfills";
import React, { useEffect, useRef, useState } from "react";
import { StatusProvider, useStatus } from "./src/providers/StatusProvider";
import { useTranslation } from "react-i18next";
import { InteractionScreen } from "./src/screens/InteractionScreen";
import { SetupScreen } from "./src/screens/SetupScreen";
import { ErrorScreen } from "./src/screens/ErrorScreen";
import { initLocalisation } from "./src/helpers/localise";
import { errorReporting } from "./src/helpers/errorReporting";
import { DeviceProvider, useDeviceId } from "./src/providers/DeviceProvider";
import { AppState, Platform, View } from "react-native";
import { startsWith } from "lodash";
import { YoutubeEmbed } from "./src/components/YoutubeVideo";
import { UpdatesProvider } from "./src/providers/UpdatesProvider";
import * as SplashScreen from "expo-splash-screen";
import { ErrorBoundary } from "react-error-boundary";
import * as Sentry from "@sentry/react-native";
import { useKeepAwake } from "expo-keep-awake";
import * as Updates from "expo-updates";
import {
  PlaylistProvider,
  usePlaylist,
} from "./src/providers/PlaylistProvider";
import { PlaylistScreen } from "./src/screens/PlaylistScreen";
import { ThemeProvider } from "./src/providers/ThemeProvider";
import {
  AppResetProvider,
  useAppReset,
} from "./src/providers/AppResetProvider";

initLocalisation();
errorReporting.initialize();
errorReporting.ignoreLogs([
  'You seem to update props of the "TRenderEngineProvider" component in short periods of time',
  "Warning: TRenderEngineProvider: Support for defaultProps will be removed",
  "Warning: MemoizedTNodeRenderer: Support for defaultProps will be removed",
  "Warning: TNodeChildrenRenderer: Support for defaultProps will be removed",
]);
SplashScreen.preventAutoHideAsync();

const App = () => {
  const appState = useRef(AppState.currentState);

  useEffect(() => {
    if (Platform.OS === "web") return;

    // reload app on opened from background
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === "active"
      ) {
        Updates.reloadAsync();
      }

      appState.current = nextAppState;
    });

    return () => subscription.remove();
  }, []);

  // The YouTube WebView integration uses this url to embed the video.
  // It triggers an "onVideoEnd" message for the WebView to listen for
  if (
    Platform.OS === "web" &&
    startsWith(window.location.pathname, "/yt-embed")
  ) {
    SplashScreen.hideAsync();
    const searchParams = new URLSearchParams(window.location.search);
    const scale = searchParams.get("scale") ?? "1";
    const loop = searchParams.get("loop") === "1";

    document
      ?.querySelector(`meta[name="viewport"]`)
      ?.setAttribute(
        "content",
        `width=device-width, initial-scale=${scale}, minimum-scale=${scale}, maximum-scale=${scale}`,
      );

    return (
      <YoutubeEmbed
        videoId={window.location.pathname.replace("/yt-embed/", "")}
        loadNextItem={() => {
          window.top?.ReactNativeWebView?.postMessage("onVideoEnd");
        }}
        loop={loop}
        onPlaybackFailed={() => {
          window.top?.ReactNativeWebView?.postMessage("onPlaybackFailed");
        }}
      />
    );
  }

  return (
    <View style={{ flex: 1 }} onLayout={() => SplashScreen.hideAsync()}>
      <KeepAwake />
      <ThemeProvider>
        <AppResetProvider>
          <DeviceProvider>
            <ErrorBoundary
              onError={(err, data) =>
                errorReporting.sendMessage(
                  `ErrorBoundary: ${err.message}`,
                  data,
                )
              }
              fallback={<ErrorScreen />}
            >
              <UpdatesProvider>
                <StatusProvider>
                  <PlaylistProvider>
                    <Routing />
                  </PlaylistProvider>
                </StatusProvider>
              </UpdatesProvider>
            </ErrorBoundary>
          </DeviceProvider>
        </AppResetProvider>
      </ThemeProvider>
    </View>
  );
};

const Routing = () => {
  const status = useStatus();
  const appReset = useAppReset();
  // if the app has been reset, we know the user has previously interacted
  const [interacted, setInteracted] = useState(appReset.resetCount > 0);
  const { t } = useTranslation("AppProvider");
  const playlist = usePlaylist();
  const deviceId = useDeviceId();

  if (status.status === "error") {
    return (
      <ErrorScreen
        message={status.reason}
        subMessages={status.subMessages}
        showResetButton={status.showResetButton}
      />
    );
  }

  if (status.status === "inactive") {
    return <SetupScreen />;
  }

  if (playlist.status === "error") {
    return (
      <ErrorScreen
        message={t("Network error: failed to load playlist")}
        subMessages={[
          playlist.message,
          t("Device ID: {{deviceId}}", { deviceId: deviceId }),
        ]}
      />
    );
  }

  if (playlist.status === "not-found") {
    return (
      <ErrorScreen
        message={t("No configuration selected")}
        showIpAddress={status.status === "hard_coded"}
        subMessages={[
          status.status === "hard_coded"
            ? t(
                "We could not find a configuration for the given code. Please contact your Teamworks administrator.",
              )
            : t(
                "This device has been registered, but no configuration has been set yet. Dont worry, you can easily update this device in the digital displays management app.",
              ),
        ]}
      />
    );
  }

  if (!playlist.skipInteractionScreen && !interacted) {
    return <InteractionScreen onInteraction={() => setInteracted(true)} />;
  }

  return <PlaylistScreen />;
};

const KeepAwake = () => {
  useKeepAwake();
  return null;
};

export default Sentry.wrap(App);
